/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { SaaSProvider, useSaaSContext } from './store/SaaSContext';
import { ThreeDProvider } from './store/ThreeDContext';
import { ToastProvider, useToast } from './store/ToastContext';
import { SaaSSidebar } from './components/saas/Sidebar';
import { SplashScreen } from './components/saas/SplashScreen';
import { ErrorBoundary } from './components/ErrorBoundary';
import { DataStep } from './components/saas/DataStep';
import { DesignStep } from './components/saas/DesignStep';
import { ImpositionStep } from './components/saas/ImpositionStep';
import { ProductionStep } from './components/saas/ProductionStep';
import { UserProfileSettings } from './components/saas/UserProfileSettings';
import { LegalModals } from './components/saas/LegalModals';
import { PreflightInspectorModal } from './components/saas/PreflightInspectorModal';
import { KeyboardShortcutsModal } from './components/saas/KeyboardShortcutsModal';
import { UserAuthDashboardModal } from './components/saas/UserAuthDashboardModal';
import { LocalAuthModal } from './components/saas/LocalAuthModal';
import { TourProvider, useTour } from './store/TourContext';
import { ProductTourModal } from './components/tour/ProductTourModal';
import { ThemeToggle } from './components/saas/ThemeToggle';
import { DevToolsSecurityShield } from './components/common/DevToolsSecurityShield';
import { ShieldAlert, Keyboard, Settings, RotateCcw, FolderOpen, UserCheck, Sparkles, Compass } from 'lucide-react';

function HeaderWidget() {
  const { 
    setShowSettings, 
    setShowAuthDashboard, 
    setShowLocalAuthModal,
    startFreshProject,
    user
  } = useSaaSContext();
  const { startTour } = useTour();
  const { toast } = useToast();

  const handleStartFreshConfirm = () => {
    startFreshProject();
    toast.success('Workspace Reset', 'Started a fresh new label canvas');
  };

  return (
    <div className="flex items-center gap-2">
      {/* Product Tour Button */}
      <button
        onClick={() => startTour(0)}
        id="btn-start-product-tour"
        className="px-2.5 py-1 bg-indigo-500/15 hover:bg-indigo-500/25 text-indigo-400 border border-indigo-500/30 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer shadow-xs"
        title="Start Guided Interactive Product Tour"
      >
        <Sparkles className="w-3.5 h-3.5 text-indigo-400 animate-pulse" />
        <span className="hidden sm:inline">Product Tour</span>
      </button>

      {/* Start Fresh Project Button */}
      <button
        onClick={handleStartFreshConfirm}
        className="px-2.5 py-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer"
        title="Start a fresh clean label canvas"
      >
        <RotateCcw className="w-3.5 h-3.5 text-orange-400" />
        <span className="hidden md:inline">Fresh Project</span>
      </button>

      {/* Library / Saved Templates Button */}
      <button
        onClick={() => setShowAuthDashboard(true)}
        className="px-2.5 py-1 bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-500/30 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer"
        title="Open Saved Templates & Preset Library"
      >
        <FolderOpen className="w-3.5 h-3.5 text-blue-400" />
        <span className="hidden sm:inline">Templates</span>
      </button>

      {/* User Session Profile Badge */}
      {user ? (
        <button
          onClick={() => setShowLocalAuthModal(true)}
          className="px-2.5 py-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 rounded-lg text-xs font-semibold flex items-center gap-2 transition-all cursor-pointer shadow-xs"
          title="Manage Local User Account"
        >
          <img src={user.avatar} alt={user.name} className="w-4 h-4 rounded-full" />
          <span className="hidden md:inline font-bold max-w-[100px] truncate">{user.name}</span>
        </button>
      ) : (
        <button
          onClick={() => setShowLocalAuthModal(true)}
          className="px-2.5 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-xs"
          title="Local Sign In / Register"
        >
          <UserCheck className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Local Sign In</span>
        </button>
      )}

      {/* Theme Switcher Button */}
      <ThemeToggle />

      {/* Settings Button */}
      <button
        onClick={() => setShowSettings(true)}
        className="p-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700 rounded-lg text-xs font-semibold flex items-center justify-center transition-all cursor-pointer"
        title="Settings"
      >
        <Settings className="w-3.5 h-3.5 text-zinc-400" />
      </button>
    </div>
  );
}

function MainLayout() {
  const [legalModalType, setLegalModalType] = useState<'privacy' | 'terms' | 'contact' | null>(null);
  const [showPreflightModal, setShowPreflightModal] = useState(false);
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);

  // Sync Legal Modals with URL Hash & Query Parameters
  useEffect(() => {
    const handleUrlChange = () => {
      const hash = window.location.hash.toLowerCase().replace('#', '');
      const searchParams = new URLSearchParams(window.location.search);
      const legalParam = searchParams.get('legal') || searchParams.get('page');

      if (hash === 'privacy' || legalParam === 'privacy') {
        setLegalModalType(prev => prev === 'privacy' ? prev : 'privacy');
      } else if (hash === 'terms' || legalParam === 'terms') {
        setLegalModalType(prev => prev === 'terms' ? prev : 'terms');
      } else if (hash === 'contact' || legalParam === 'contact') {
        setLegalModalType(prev => prev === 'contact' ? prev : 'contact');
      }
    };

    handleUrlChange();
    window.addEventListener('hashchange', handleUrlChange);
    window.addEventListener('popstate', handleUrlChange);
    return () => {
      window.removeEventListener('hashchange', handleUrlChange);
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, []);

  const handleOpenLegal = (type: 'privacy' | 'terms' | 'contact') => {
    setLegalModalType(type);
    try {
      window.location.hash = type;
    } catch {}
  };

  const handleCloseLegal = () => {
    setLegalModalType(null);
    try {
      if (window.location.hash) {
        history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    } catch {}
  };
  
  const { 
    activeStep,
    setActiveStep,
    template, 
    csvData, 
    layout, 
    showAuthDashboard, 
    setShowAuthDashboard,
    showLocalAuthModal,
    setShowLocalAuthModal,
    setTemplate,
    setLayout,
    setAppUnit,
    isInitializing,
    setIsInitializing,
    theme
  } = useSaaSContext();

  const handleFinishSplash = React.useCallback(() => {
    setIsInitializing(false);
  }, [setIsInitializing]);

  const handleLoadTemplateFromModal = (newTpl: any, newLayout?: any, newUnit?: any) => {
    if (newTpl) setTemplate(newTpl);
    if (newLayout) setLayout(newLayout);
    if (newUnit) setAppUnit(newUnit);
  };

  return (
    <ErrorBoundary>
      {isInitializing && <SplashScreen onFinish={handleFinishSplash} />}
    <div className={`flex h-screen w-full font-sans text-zinc-100 ${theme === 'glassmorphism' ? 'theme-glassmorphism bg-transparent' : 'bg-zinc-950'}`}>
      <SaaSSidebar activeStep={activeStep} setActiveStep={setActiveStep} />
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <header className="flex mt-14 lg:mt-0 h-14 bg-zinc-900 border-b border-zinc-800 items-center justify-between px-3 sm:px-5 shrink-0 gap-3 relative z-30">
          {/* Zone 1: Document Spec & Breadcrumbs */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center gap-2 text-xs">
              <span className="font-bold text-zinc-100 uppercase tracking-wider text-xs whitespace-nowrap">
                {activeStep === 'data' ? '1. Data Source' : activeStep === 'design' ? '2. Template Studio' : activeStep === 'imposition' ? '3. Imposition Matrix' : '4. Production & Cut'}
              </span>
              <span className="text-zinc-600 hidden sm:inline">/</span>
              <div className="hidden md:flex items-center gap-2 text-zinc-400 font-mono text-[11px]">
                <span className="truncate max-w-[140px] text-zinc-300 font-semibold">{template.name || 'Custom Label'}</span>
                <span>•</span>
                <span>{template.width || 100} × {template.height || 60} {template.unit || 'mm'}</span>
                <span>•</span>
                <span className="text-blue-400">{csvData ? `${csvData.rows.length} Records` : 'Static Mode'}</span>
              </div>
            </div>

            <div className="hidden lg:flex items-center gap-1.5 border-l border-zinc-800 pl-3">
              <button
                onClick={() => setShowPreflightModal(true)}
                className="px-2.5 py-1 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer shadow-2xs"
                title="Run automated pre-flight quality checks"
              >
                <ShieldAlert className="w-3.5 h-3.5 text-emerald-400" />
                <span className="hidden xl:inline">Pre-Flight Pass</span>
              </button>
              <button
                onClick={() => setShowShortcutsModal(true)}
                className="p-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700 rounded-lg text-xs flex items-center gap-1 transition-all cursor-pointer"
                title="Keyboard Shortcuts (Hotkeys: V, H, T, P, Ctrl+Z, Ctrl+D)"
              >
                <Keyboard className="w-3.5 h-3.5 text-zinc-400" />
              </button>
            </div>
          </div>

          {/* Zone 3: Quick Action Tools & Profile */}
          <div className="flex items-center gap-2 shrink-0">
            <HeaderWidget />
          </div>
        </header>
        <main className={`flex-1 relative ${(activeStep === 'imposition' || activeStep === 'design') ? 'overflow-hidden p-0 flex flex-col min-h-0' : 'overflow-auto p-3 sm:p-4 md:p-6'}`}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="h-full w-full flex flex-col"
            >
              <React.Suspense fallback={<div className="flex h-full items-center justify-center text-zinc-500 font-medium">Loading interface module...</div>}>
                {activeStep === 'data' && <DataStep onNext={() => setActiveStep('design')} />}
                {activeStep === 'design' && <DesignStep />}
                {activeStep === 'imposition' && <ImpositionStep />}
                {activeStep === 'production' && <ProductionStep />}
              </React.Suspense>
            </motion.div>
          </AnimatePresence>
        </main>
        <footer className="h-10 bg-zinc-950 border-t border-zinc-900 flex items-center justify-between px-4 shrink-0 text-[10px] text-zinc-500 gap-4">
          <div className="flex items-center gap-2 text-zinc-400 font-medium">
            <span>&copy; 2026 Variable Sticker Studio. All rights reserved.</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setIsInitializing(true)} className="hover:text-amber-300 text-amber-400/80 transition-colors cursor-pointer" title="Replay Animated Splash Screen">Splash Screen</button>
            <button onClick={() => handleOpenLegal('contact')} className="hover:text-zinc-300 transition-colors cursor-pointer">Contact Us</button>
            <button onClick={() => handleOpenLegal('privacy')} className="hover:text-zinc-300 transition-colors cursor-pointer">Privacy Policy</button>
            <button onClick={() => handleOpenLegal('terms')} className="hover:text-zinc-300 transition-colors cursor-pointer">Terms of Service</button>
          </div>
        </footer>
      </div>
      <UserProfileSettings />
      <LocalAuthModal
        isOpen={showLocalAuthModal}
        onClose={() => setShowLocalAuthModal(false)}
      />
      <UserAuthDashboardModal
        isOpen={showAuthDashboard}
        onClose={() => setShowAuthDashboard(false)}
        onLoadTemplate={handleLoadTemplateFromModal}
      />
      <LegalModals type={legalModalType} onClose={handleCloseLegal} />
      <PreflightInspectorModal
        isOpen={showPreflightModal}
        onClose={() => setShowPreflightModal(false)}
        template={template}
        csvData={csvData}
        layout={layout}
      />
      <KeyboardShortcutsModal
        isOpen={showShortcutsModal}
        onClose={() => setShowShortcutsModal(false)}
      />
      <ProductTourModal />
    </div>
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <ToastProvider>
      <SaaSProvider>
        <ThreeDProvider>
          <TourProvider>
            <DevToolsSecurityShield>
              <MainLayout />
            </DevToolsSecurityShield>
          </TourProvider>
        </ThreeDProvider>
      </SaaSProvider>
    </ToastProvider>
  );
}

